/**
 * MarketingConsentSection.tsx — the deal drawer's "Marketing consent" section
 * (Overview tab, under Contact), for ADMIN and CLINICIAN.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: marketing consent (2026-09-11).
 *
 * WHAT STAFF SEE: one row per channel — "Given 24 September 2026 ·
 * Questionnaire consent step", "Withdrawn … · Unsubscribe link", "Not given" —
 * and the ledger itself behind a disclosure, newest first, so "why are we (not)
 * emailing this person?" has an answer with a date and a source. The data is
 * clinic-service's `GET /v1/clinic/deals/:id/marketing-consent`.
 *
 * ⚠️ STAFF CAN WITHDRAW AND CAN NEVER GRANT. Consent has to come from the person
 * (UK GDPR Art 7, PECR reg. 22), so there is no grant control anywhere in this
 * file and there must never be one — not a checkbox, not a "restore" button, not
 * a menu row. "Record a withdrawal" exists because people tell us on the phone,
 * and the ask must be honoured without making them find an email. It needs a
 * reason (3..500 characters) that lands on the `marketing_consent_withdrawn`
 * AuditEntry clinic-service writes in the same transaction as the ledger row.
 *
 * ⚠️ AN UNKNOWN IS NOT A NO. A failed read renders "Consent status unavailable"
 * and NEVER "Not given": a desk reading "Not given" off an outage would tell a
 * patient who opted in that they had not, or decide not to act on an opt-out
 * they cannot see. Same rule as the payment posture's three absences.
 *
 * The confirm uses the DEFAULT (green) tone, on purpose: the consequence is that
 * we stop sending someone marketing they asked us to stop — nothing is lost to
 * the patient, and the red is kept for consequences like refunds and deletions.
 *
 * ── 2026-09-26, returning patients S4.5 (clinic S4.3 #226; CTO Q10/Q11) ──────
 *  - ONE "Mark withdrawn" BUTTON PER GRANTED CHANNEL, on that channel's row
 *    (Q11: "each channel gets its own button with a confirm step and a
 *    reason"). The single "Record a withdrawal" button and its radio group are
 *    gone: the row already says WHICH channel, so the dialog names it and asks
 *    only for the reason — there is no second choice to get wrong.
 *  - "RE-CONFIRMED" ENTRIES (Q10): a ledger row with `kind: 'reaffirmed'` is the
 *    patient re-ticking a channel that was already granted. It reads "Email
 *    re-confirmed", and the channel's row adds "· Re-confirmed <date>" when the
 *    newest re-confirmation is later than the last state change. An ABSENT
 *    `kind` (a clinic-service older than S4.3) is a state change, never a
 *    re-confirmation nobody made.
 *  - STAFF NAMES FOR ADMIN ONLY (Q11: "admins see which staff member did it").
 *    Only an ADMIN's rows carry `actorUserId`; the section resolves them through
 *    the ADMIN-only batched staff lookup and prints "Recorded by <name>". A
 *    CLINICIAN issues no lookup and keeps "Recorded by staff"; a `svc:` machine
 *    id is never looked up and never shown as a person (the lookup also strips
 *    it at the API boundary).
 *  - `lead_capture` has words ("Details form (start of questionnaire)").
 */
import { useEffect, useId, useState } from 'react';
import { ApiError } from '../api/client';
import { getDealMarketingConsent, withdrawDealMarketingConsent } from '../api/deals';
import { isServicePrincipal, lookupStaffDisplayMap } from '../api/identity';
import type {
  ConsentSource,
  MarketingChannel,
  MarketingChannelState,
  MarketingConsentHistoryRow,
  StaffMarketingConsent,
  UserRole,
} from '../api/types';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Button } from '../ui/kit';
import { compactActionStyle } from '../staff/ui';
import { Row, SectionTitle } from '../ui/sections';

/**
 * Every ledger source in words. Exhaustive on purpose: a new server source is a
 * COMPILE error here, not a raw enum on a staff screen.
 *
 * `journey_result` keeps "Result screen" although nothing is recorded there any
 * more: it is only ever carried by rows written BEFORE the 2026-09-23 move, and
 * for every one of those it is exactly where the patient ticked. The date on
 * the row is what places it; a label saying "old" would be a second, weaker
 * version of the same fact.
 */
export const CONSENT_SOURCE_LABELS: Record<ConsentSource, string> = {
  journey_consent_step: 'Questionnaire consent step',
  journey_result: 'Result screen',
  // The Step-0 mini form's own box (clinic #218). "Details form" is what staff
  // call that first screen; the bracket says where it sits.
  lead_capture: 'Details form (start of questionnaire)',
  preferences_page: 'Preferences page',
  unsubscribe_link: 'Unsubscribe link',
  unsubscribe_one_click: 'One-click unsubscribe (email app)',
  staff: 'Recorded by staff',
};

const CHANNEL_LABELS: Record<MarketingChannel, string> = { email: 'Email', whatsapp: 'WhatsApp' };

/** A source in words; a value this build has not heard of says so rather than rendering the enum. */
export function sourceLabel(source: ConsentSource | null): string | null {
  if (!source) return null;
  return CONSENT_SOURCE_LABELS[source] ?? 'Source not recognised';
}

/**
 * One channel's row. "Not given" ONLY where the server has no ledger row at all
 * (never answered); a false with a row behind it is a withdrawal, and says when
 * and how.
 */
export function channelSummary(state: MarketingChannelState, reconfirmedAt?: string | null): string {
  if (!state.granted && state.changedAt === null && state.source === null) return 'Not given';
  const verb = state.granted ? 'Given' : 'Withdrawn';
  const when = formatUtcDate(state.changedAt);
  const from = sourceLabel(state.source);
  const head = when ? `${verb} ${when}` : verb;
  const summary = from ? `${head} · ${from}` : head;
  // A re-confirmation only means something on a channel that is still granted.
  const again = state.granted && reconfirmedAt ? formatUtcDate(reconfirmedAt) : '';
  return again ? `${summary} · Re-confirmed ${again}` : summary;
}

/** Was this ledger row a re-tick of an already-granted channel? Absent `kind` = a state change. */
export function isReaffirmation(row: Pick<MarketingConsentHistoryRow, 'kind'>): boolean {
  return row.kind === 'reaffirmed';
}

/**
 * The newest re-confirmation of a channel that is LATER than its last state
 * change, or null. Rows arrive newest first. Later-than matters: a
 * re-confirmation made before a withdrawal and a new grant says nothing about
 * the grant standing now.
 */
export function latestReconfirmation(consent: StaffMarketingConsent, channel: MarketingChannel): string | null {
  const changedAt = consent[channel].changedAt;
  const row = consent.history.find((r) => r.channel === channel && isReaffirmation(r));
  if (!row) return null;
  if (changedAt && Date.parse(row.occurredAt) <= Date.parse(changedAt)) return null;
  return row.occurredAt;
}

/** A row's actor id, when it names a PERSON the ADMIN lookup could resolve. */
function staffActorOf(row: MarketingConsentHistoryRow): string | null {
  const id = typeof row.actorUserId === 'string' ? row.actorUserId.trim() : '';
  return id && !isServicePrincipal(id) ? id : null;
}

type Load = { kind: 'loading' } | { kind: 'unavailable' } | { kind: 'ready'; consent: StaffMarketingConsent };

const REASON_MAX = 500;

export function MarketingConsentSection({ dealId, role }: { dealId: string; role?: UserRole }) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  /** The channel whose "Mark withdrawn" was pressed — the dialog is open while it is set. */
  const [channel, setChannel] = useState<MarketingChannel | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [staffNames, setStaffNames] = useState<Record<string, string>>({});
  const errorId = useId();
  const isAdmin = role === 'ADMIN';

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    getDealMarketingConsent(dealId).then(
      (consent) => {
        if (!cancelled) setLoad({ kind: 'ready', consent });
      },
      () => {
        if (!cancelled) setLoad({ kind: 'unavailable' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [dealId, nonce]);

  // ADMIN ONLY: resolve the staff members who recorded a row, in ONE batched
  // lookup per distinct set. A CLINICIAN never issues it (the route is
  // ADMIN-only and their rows carry no actor anyway); a failed lookup leaves
  // the rows reading "Recorded by staff", never an error.
  const actorKey =
    isAdmin && load.kind === 'ready'
      ? [...new Set(load.consent.history.map(staffActorOf).filter((id): id is string => id !== null))].sort().join(',')
      : '';
  useEffect(() => {
    if (!actorKey) return;
    let cancelled = false;
    lookupStaffDisplayMap(actorKey.split(','))
      .then((map) => {
        if (!cancelled) setStaffNames((prev) => ({ ...prev, ...map }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [actorKey]);

  /** A row's "where from", naming the staff member for an ADMIN when we can. */
  function rowSource(row: MarketingConsentHistoryRow): string {
    const actor = isAdmin ? staffActorOf(row) : null;
    const name = actor ? staffNames[actor] : undefined;
    if (row.source === 'staff' && name) return `Recorded by ${name}`;
    return sourceLabel(row.source) ?? 'Source not recorded';
  }

  function openDialog(which: MarketingChannel) {
    setChannel(which);
    setError(null);
    setNotice(null);
  }

  function confirmWithdrawal(reason?: string) {
    if (!channel || !reason || busy) return;
    setBusy(true);
    setError(null);
    withdrawDealMarketingConsent(dealId, { channel, reason }).then(
      (consent) => {
        setBusy(false);
        setChannel(null);
        // The POST answers the same shape as the GET — the state AFTER the write.
        setLoad({ kind: 'ready', consent });
        setNotice(`${CHANNEL_LABELS[channel]} consent withdrawal recorded.`);
      },
      (err: unknown) => {
        setBusy(false);
        // The dialog stays open with the reason kept: a refusal is correctable in place.
        setError(
          err instanceof ApiError && err.detail
            ? `We couldn't record the withdrawal: ${err.detail}`
            : "We couldn't record the withdrawal. Nothing has been changed on our side that we know of — please try again.",
        );
      },
    );
  }

  return (
    <div style={{ marginTop: 'var(--gps-s-4)' }}>
      <SectionTitle>Marketing consent</SectionTitle>

      {load.kind === 'loading' ? (
        // Not a live region on purpose: the drawer already announces its own
        // loading, and a second polite region per section is noise.
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--gps-muted)' }}>Checking consent…</p>
      ) : null}

      {load.kind === 'unavailable' ? (
        <div>
          <p style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 600, color: 'var(--gps-text)' }}>
            Consent status unavailable
          </p>
          <p style={{ margin: '0 0 8px', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
            We could not read it just now. This does not mean the patient said no.
          </p>
          <Button type="button" variant="ghost" style={{ width: 'auto' }} onClick={() => setNonce((n) => n + 1)}>
            Try again
          </Button>
        </div>
      ) : null}

      {load.kind === 'ready' ? (
        <>
          {(['email', 'whatsapp'] as const).map((which) => {
            const state = load.consent[which];
            const summary = channelSummary(state, latestReconfirmation(load.consent, which));
            const shown =
              which === 'whatsapp' && !load.consent.whatsappAvailable ? `${summary} · No UK mobile on file` : summary;
            return (
              <Row
                key={which}
                label={CHANNEL_LABELS[which]}
                value={
                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
                    <span>{shown}</span>
                    {/* Only on a GRANTED channel: there is nothing to withdraw
                        otherwise, and no grant control to offer in its place. */}
                    {state.granted ? (
                      <Button
                        type="button"
                        variant="ghost"
                        // The estate's compact staff action (staff/ui.tsx): small to
                        // look at, still a 44px target, so two rows of it do not
                        // dwarf the consent facts they sit beside.
                        style={compactActionStyle}
                        onClick={() => openDialog(which)}
                      >
                        Mark withdrawn
                        {/* The visible words first (WCAG 2.5.3), the channel for a
                            screen reader, so two buttons are two names. */}
                        <span className="gps-sr-only"> — {CHANNEL_LABELS[which]}</span>
                      </Button>
                    ) : null}
                  </span>
                }
              />
            );
          })}

          {load.consent.history.length > 0 ? (
            <details style={{ margin: '4px 0 8px' }}>
              <summary style={{ cursor: 'pointer', padding: '12px 0', fontSize: '14px', color: 'var(--gps-link)' }}>
                Consent history ({load.consent.history.length})
              </summary>
              {/* The server sends these newest first; rendered in its order. */}
              <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {load.consent.history.map((row, index) => (
                  <li
                    key={`${row.occurredAt}-${row.channel}-${index}`}
                    style={{ padding: '8px 0', borderTop: '1px solid var(--gps-border)', fontSize: '13.5px', color: 'var(--gps-text)' }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {CHANNEL_LABELS[row.channel] ?? row.channel}{' '}
                      {isReaffirmation(row) ? 're-confirmed' : row.granted ? 'given' : 'withdrawn'}
                    </span>
                    {` · ${rowSource(row)}`}
                    <span style={{ display: 'block', color: 'var(--gps-muted)' }}>
                      {formatUtcDateTime(row.occurredAt)}
                      {row.wordingVersion ? ` · wording ${row.wordingVersion}` : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </details>
          ) : (
            <p style={{ margin: '4px 0 8px', fontSize: '13.5px', color: 'var(--gps-muted)' }}>No consent changes recorded.</p>
          )}

          {/* Mounted for as long as the rows are, and filled in after a withdrawal:
              a live region inserted with its text already inside is not reliably
              announced (WCAG 4.1.3), and a withdrawal only ever happens from here. */}
          <p role="status" style={{ margin: notice ? '8px 0 0' : 0, fontSize: '13.5px', color: 'var(--gps-success-text)' }}>
            {notice}
          </p>
        </>
      ) : null}

      <ConfirmDialog
        open={channel !== null}
        title={channel ? `Mark ${channel === 'email' ? 'email' : 'WhatsApp'} consent as withdrawn?` : 'Mark consent as withdrawn?'}
        confirmLabel="Record withdrawal"
        busy={busy}
        requireReason
        reasonMaxLength={REASON_MAX}
        confirmDescribedBy={error ? errorId : undefined}
        onCancel={() => setChannel(null)}
        onConfirm={confirmWithdrawal}
        body={
          <>
            <p style={{ margin: '0 0 12px' }}>
              Use this when the patient has told us they no longer want marketing messages by{' '}
              {channel === 'whatsapp' ? 'WhatsApp' : 'email'}. It stops those messages. Only the patient can give
              consent again — staff cannot.
            </p>
            <p style={{ margin: '0 0 8px', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
              In the reason, say how the patient told us — for example, &ldquo;Patient asked on the phone&rdquo;.
            </p>
            {error ? (
              <Alert tone="danger" role="alert" id={errorId} style={{ marginBottom: '8px' }}>
                {error}
              </Alert>
            ) : null}
          </>
        }
      />
    </div>
  );
}
